import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";
import { api } from "@/lib/api";
import { UsersThree, Clock, ChatCircle, UserFocus, Trophy, Warning, UserCircle } from "@phosphor-icons/react";

function pColor(p) {
  if (p >= 85) return "#32D74B";
  if (p >= 70) return "#007AFF";
  if (p >= 55) return "#FF9F0A";
  return "#FF3B30";
}
const firstName = (n) => (n || "").trim().split(/\s+/)[0] || n;

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#71717A] mb-1">
        <Icon size={14} weight="bold" style={color ? { color } : undefined} /> {label}
      </div>
      <div className="font-mono-data text-3xl" style={color ? { color } : undefined}>{value}</div>
    </div>
  );
}

export default function Employees() {
  const [emps, setEmps] = useState([]);
  const [unident, setUnident] = useState({ people: 0, active: 0, productivity: 0, active_minutes: 0, interactions: 0 });

  const load = () => api.get("/employees").then((r) => {
    // backwards compatible: old shape was a bare array
    const d = r.data;
    if (Array.isArray(d)) { setEmps(d.filter((e) => !/unidentified/i.test(e.name))); }
    else { setEmps(d.employees || []); setUnident(d.unidentified || {}); }
  }).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const chartData = emps.map((e) => ({ name: firstName(e.name), productivity: e.productivity }));
  const active = emps.filter((e) => e.status === "active");
  const top = emps[0];
  const needs = [...emps].filter((e) => e.productivity > 0).sort((a, b) => a.productivity - b.productivity)[0];

  return (
    <div className="space-y-4" data-testid="employees-page">
      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-[#121214] border border-white/10 p-4"><Stat icon={UsersThree} label="Named Staff" value={emps.length} /></div>
        <div className="bg-[#121214] border border-white/10 p-4"><Stat icon={UserFocus} label="On Floor Now" value={active.length} color="#32D74B" /></div>
        <div className="bg-[#121214] border border-white/10 p-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#71717A] mb-1"><Trophy size={14} weight="bold" style={{ color: "#FFD60A" }} /> Top Performer</div>
          <div className="font-mono-data text-xl truncate">{top ? firstName(top.name) : "—"}</div>
          <div className="text-xs font-mono-data mt-0.5" style={{ color: top ? pColor(top.productivity) : "#71717A" }}>{top ? `${top.productivity}%` : "tag staff to rank"}</div>
        </div>
        <div className="bg-[#121214] border border-white/10 p-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#71717A] mb-1"><Warning size={14} weight="bold" style={{ color: "#FF9F0A" }} /> Needs Attention</div>
          <div className="font-mono-data text-xl truncate">{needs ? firstName(needs.name) : "—"}</div>
          <div className="text-xs font-mono-data mt-0.5" style={{ color: needs ? pColor(needs.productivity) : "#71717A" }}>{needs ? `${needs.productivity}%` : "—"}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-[#121214] border border-white/10 p-4">
          <h2 className="font-display font-black uppercase tracking-tighter text-sm mb-1">Productivity Score — Named Staff</h2>
          <p className="text-xs text-[#71717A] mb-3">Only tagged employees are ranked here. Tag faces on People &amp; ID and they appear automatically.</p>
          {chartData.length === 0 ? (
            <div className="h-[220px] flex flex-col items-center justify-center text-center gap-2 text-[#71717A]" data-testid="employees-empty">
              <UserCircle size={40} weight="thin" />
              <div className="font-mono-data text-xs">No named staff yet</div>
              <div className="text-xs max-w-xs">Go to People &amp; ID, tag a captured face with a name, and their live productivity shows up here.</div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 8, left: -16 }}>
                <XAxis dataKey="name" tick={{ fill: "#A1A1AA", fontSize: 11, fontFamily: "IBM Plex Mono" }} axisLine={{ stroke: "#27272A" }} tickLine={false} interval={0} angle={-30} textAnchor="end" height={60} />
                <YAxis tick={{ fill: "#71717A", fontSize: 11, fontFamily: "IBM Plex Mono" }} axisLine={false} tickLine={false} domain={[0, 100]} />
                <Tooltip cursor={{ fill: "rgba(255,255,255,0.05)" }} contentStyle={{ background: "#1A1A1E", border: "1px solid #27272A", borderRadius: 0, fontFamily: "IBM Plex Mono", fontSize: 12 }} />
                <Bar dataKey="productivity" radius={[2, 2, 0, 0]}>
                  {chartData.map((d, i) => <Cell key={i} fill={pColor(d.productivity)} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Unidentified activity rolled into ONE card */}
        <div className="bg-[#121214] border border-white/10 p-4 flex flex-col" data-testid="unidentified-activity">
          <h2 className="font-display font-black uppercase tracking-tighter text-sm mb-1 flex items-center gap-2">
            <UserFocus size={16} weight="bold" className="text-[#64D2FF]" /> Unidentified Activity
          </h2>
          <p className="text-xs text-[#71717A] mb-4">Untagged people on the floor, rolled up so they don't clutter the ranking. Tag them to break them out individually.</p>
          <div className="grid grid-cols-2 gap-4">
            <Stat icon={UsersThree} label="Untagged" value={unident.people || 0} color="#64D2FF" />
            <Stat icon={UserFocus} label="Active Now" value={unident.active || 0} color="#32D74B" />
            <Stat icon={Clock} label="Floor Time" value={`${unident.active_minutes || 0}m`} />
            <Stat icon={ChatCircle} label="Interactions" value={unident.interactions || 0} />
          </div>
          <div className="mt-auto pt-4">
            <div className="text-xs uppercase tracking-widest text-[#71717A] mb-1">Avg Productivity</div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-black/60 overflow-hidden"><div className="h-full" style={{ width: `${unident.productivity || 0}%`, background: pColor(unident.productivity || 0) }} /></div>
              <span className="font-mono-data" style={{ color: pColor(unident.productivity || 0) }}>{unident.productivity || 0}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Named staff log with photos */}
      <div className="bg-[#121214] border border-white/10">
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
          <h2 className="font-display font-black uppercase tracking-tighter text-sm">Staff Performance</h2>
          <span className="font-mono-data text-xs text-[#71717A]">{emps.length} tagged</span>
        </div>
        {emps.length === 0 ? (
          <div className="px-4 py-10 text-center text-[#71717A] text-sm" data-testid="employees-table-empty">
            No tagged employees yet. Tag faces on <span className="text-white">People &amp; ID</span> to start measuring individual performance.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="employees-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-[#71717A] border-b border-white/10">
                <th className="px-4 py-2 font-medium">Employee</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Last Seen</th>
                <th className="px-4 py-2 font-medium">Productivity</th>
                <th className="px-4 py-2 font-medium font-mono-data">Active</th>
                <th className="px-4 py-2 font-medium font-mono-data">Idle</th>
                <th className="px-4 py-2 font-medium font-mono-data">Interactions</th>
              </tr>
            </thead>
            <tbody>
              {emps.map((e) => (
                <tr key={e.id} data-testid={`employee-row-${e.id}`} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {e.rep_image_b64
                        ? <img src={`data:image/jpeg;base64,${e.rep_image_b64}`} alt={e.name} className="w-9 h-9 object-cover border border-white/10" />
                        : <div className="w-9 h-9 bg-white/5 border border-white/10 flex items-center justify-center"><UserCircle size={20} weight="thin" className="text-[#71717A]" /></div>}
                      <div>
                        <div className="font-medium flex items-center gap-1.5">
                          {e.name}
                          {e.face_ready === false && (
                            <span data-testid={`employee-no-faceid-${e.id}`}
                              title={e.face_issue === "no-photo"
                                ? "No reference photo on file — add a clear, frontal face photo on People & ID so cameras can recognise this person."
                                : "The stored photo has no detectable face — replace it on People & ID with a clear, frontal, well-lit face photo."}
                              className="font-mono-data text-[9px] px-1.5 py-0.5 bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/40 uppercase tracking-wider flex items-center gap-1">
                              <Warning size={9} weight="bold" /> No Face ID
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-[#71717A]">{e.role}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`font-mono-data text-[10px] px-1.5 py-0.5 border uppercase ${e.status === "active" ? "text-[#32D74B] border-[#32D74B]/40 bg-[#32D74B]/10" : "text-[#71717A] border-[#71717A]/40"}`}>{e.status}</span>
                  </td>
                  <td className="px-4 py-3 font-mono-data text-[10px] text-[#A1A1AA]">{e.last_camera || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-1.5 bg-black/60 overflow-hidden"><div className="h-full" style={{ width: `${e.productivity}%`, background: pColor(e.productivity) }} /></div>
                      <span className="font-mono-data" style={{ color: pColor(e.productivity) }}>{e.productivity}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono-data text-[#A1A1AA]">{e.active_minutes}m</td>
                  <td className="px-4 py-3 font-mono-data text-[#A1A1AA]">{e.idle_minutes}m</td>
                  <td className="px-4 py-3 font-mono-data text-[#A1A1AA]">{e.interactions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
